import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer, restoreStashedSelectOptions, detachHiddenSelectOptions, parseJsonResponse, fetchCartJsonWithMeta, CART_RATE_LIMIT_MESSAGE } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { formatCartonPerDaysLabel } from '@theme/nro-labels';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const USAGE_HARD_CEILING = 12;

/**
 * Mirror of usageProducesVisiblePlan / computeUsageRange in variant-picker.js
 * so the cart NRO panel applies the same usage-range constraint as the PDP.
 *
 * @param {number} usage
 * @param {number} planDays
 * @param {number} piecesPerVariant
 * @param {number} nroMinPieces
 * @param {number} nroMaxPacks
 * @param {number} toleranceDays
 */
function usageProducesVisiblePlan(usage, planDays, piecesPerVariant, nroMinPieces, nroMaxPacks, nroMaxCartonCount, toleranceDays) {
  if (piecesPerVariant <= 0 || !Number.isFinite(planDays) || planDays <= 0) return false;
  const cartonCount = Math.max(1, Math.round((planDays * usage) / piecesPerVariant));
  const expectedPlanDays = (cartonCount * piecesPerVariant) / usage;
  if (Math.abs(planDays - expectedPlanDays) > toleranceDays) return false;
  if (nroMinPieces > 0 && cartonCount * piecesPerVariant < nroMinPieces) return false;
  if (nroMaxPacks > 0 && cartonCount > nroMaxPacks) return false;
  if (nroMaxCartonCount > 0 && cartonCount > nroMaxCartonCount) return false;
  return true;
}

function computeUsageRange(cfg) {
  const {
    piecesPerVariant,
    planDaysValues,
    nroMinPieces,
    nroMaxPacks,
    nroMaxCartonCount,
    maxUsage: configuredMaxUsage,
    toleranceDays,
  } = cfg;
  const usageCeiling = Number.isFinite(configuredMaxUsage) && configuredMaxUsage > 0
    ? Math.floor(configuredMaxUsage)
    : USAGE_HARD_CEILING;
  if (!planDaysValues || planDaysValues.length === 0 || piecesPerVariant <= 0) {
    return { minUsage: 1, maxUsage: usageCeiling };
  }
  let minUsage = 0;
  let maxUsage = 0;
  for (let u = 1; u <= usageCeiling; u++) {
    const fits = planDaysValues.some((d) =>
      usageProducesVisiblePlan(u, d, piecesPerVariant, nroMinPieces, nroMaxPacks, nroMaxCartonCount, toleranceDays)
    );
    if (fits) {
      if (minUsage === 0) minUsage = u;
      maxUsage = u;
    }
  }
  if (minUsage === 0) return { minUsage: 1, maxUsage: 1 };
  return { minUsage, maxUsage };
}

function syncCartNroUsageRange(usageSelect, cfg) {
  restoreStashedSelectOptions(usageSelect);
  const { minUsage, maxUsage } = computeUsageRange(cfg);
  let anyVisible = false;
  let firstVisible = null;
  for (const option of usageSelect.options) {
    const value = Number.parseInt(option.value || '0', 10);
    const inRange = value >= minUsage && value <= maxUsage;
    option.hidden = !inRange;
    option.disabled = !inRange;
    if (inRange) {
      anyVisible = true;
      if (firstVisible == null) firstVisible = String(value);
    }
  }
  detachHiddenSelectOptions(usageSelect);
  if (!anyVisible) return;
  const currentValue = Number.parseInt(usageSelect.value || '0', 10);
  if (currentValue < minUsage || currentValue > maxUsage) {
    if (currentValue < minUsage) usageSelect.value = String(minUsage);
    else usageSelect.value = String(maxUsage);
  } else if (!usageSelect.value && firstVisible != null) {
    usageSelect.value = firstVisible;
  }
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('click', this.#handleNroClick);
    this.addEventListener('change', this.#handleNroChange);
    this.#initNroControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('click', this.#handleNroClick);
    this.removeEventListener('change', this.#handleNroChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {string|number|null} [config.sellingPlan] - Selling plan id when converting to NRO.
   * @param {Record<string, string>} [config.properties] - Line item properties to persist on the cart line.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, sellingPlan, properties } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    /** @type {{line:number, quantity:number, sections:string, sections_url:string, selling_plan?: string|number|null, properties?: Record<string, string>}} */
    const payload = {
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    if (sellingPlan !== undefined) {
      payload.selling_plan = sellingPlan;
    }

    if (properties && Object.keys(properties).length > 0) {
      payload.properties = properties;
    }

    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => parseJsonResponse(response))
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
        this.#initNroControls();
      })
      .catch((error) => {
        resetShimmer(this);
        this.#handleCartError(line, {
          errors: /** @type {any} */ (error)?.status === 429
            ? CART_RATE_LIMIT_MESSAGE
            : (error?.message || 'Unable to update cart.'),
        });
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs.cartItemErrors?.[line - 1];
    const cartItemErrorContainer = this.refs.cartItemErrorContainers?.[line - 1];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      void morphSection(this.sectionId, cartItemsHtml).then((didMorph) => {
        if (!didMorph) {
          void this.#refreshCartSectionAfterStaleAdd();
          return;
        }

        this.#updateCartQuantitySelectorButtonStates();
        this.#initNroControls();
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Re-fetch cart section HTML when cart/add returned stale empty markup.
   */
  async #refreshCartSectionAfterStaleAdd() {
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
      }

      try {
        const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false);
        if (await morphSection(this.sectionId, sectionHTML)) {
          this.#updateCartQuantitySelectorButtonStates();
          this.#initNroControls();
          return;
        }
      } catch (error) {
        const errorMessage = String(/** @type {any} */ (error)?.message || '');
        if (/** @type {any} */ (error)?.status === 429 || errorMessage.includes('429')) {
          return;
        }
      }
    }

    try {
      const { cart, rateLimited } = await fetchCartJsonWithMeta();
      if (rateLimited) return;

      if (cart?.item_count > 0) {
        const sectionHTML = await sectionRenderer.getSectionHTML(this.sectionId, false);
        if (await morphSection(this.sectionId, sectionHTML, { force: true })) {
          this.#updateCartQuantitySelectorButtonStates();
          this.#initNroControls();
        }
      }
    } catch (error) {
      console.error('Failed to force refresh cart section after stale add:', error);
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  #initNroControls() {
    this.querySelectorAll('[data-cart-nro]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      this.#hydrateNroPlanDays(root);
      this.#updateNroPlanLabels(root);
      this.#updateNroDiscountLabel(root);
    });
  }

  #handleNroClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const toggle = event.target.closest('[data-cart-nro-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      const root = toggle.closest('[data-cart-nro]');
      const panel = root?.querySelector('[data-cart-nro-panel]');
      if (!(root instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      // Re-run NRO math when opening so labels/options are always normalized after reload/morph.
      this.#hydrateNroPlanDays(root);
      this.#updateNroPlanLabels(root);
      this.#updateNroDiscountLabel(root);

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!isExpanded));
      this.#toggleNroPanel(panel, !isExpanded);
      return;
    }

    const applyButton = event.target.closest('[data-cart-nro-apply]');
    if (applyButton instanceof HTMLButtonElement) {
      const root = applyButton.closest('[data-cart-nro]');
      if (!(root instanceof HTMLElement)) return;
      this.#applyNroSelection(root, applyButton);
    }
  };

  #handleNroChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    const root = event.target.closest('[data-cart-nro]');
    if (!(root instanceof HTMLElement)) return;

    if (event.target.matches('[data-cart-nro-usage]')) {
      this.#updateNroPlanLabels(root);
      const planSelect = root.querySelector('[data-cart-nro-plan]');
      if (planSelect instanceof HTMLSelectElement) {
        const firstVisibleOption = Array.from(planSelect.options).find((option) => !option.hidden);
        if (firstVisibleOption) {
          planSelect.value = firstVisibleOption.value;
        }
      }
      this.#updateNroDiscountLabel(root);
      return;
    }

    if (event.target.matches('[data-cart-nro-plan]')) {
      this.#updateNroDiscountLabel(root);
    }
  };

  #applyNroSelection(root, applyButton) {
    const line = Number.parseInt(root.dataset.line || '', 10);
    const planSelect = root.querySelector('[data-cart-nro-plan]');
    const usageSelect = root.querySelector('[data-cart-nro-usage]');
    if (Number.isNaN(line) || !(planSelect instanceof HTMLSelectElement)) return;

    const selectedOption = planSelect.options[planSelect.selectedIndex];
    if (!selectedOption?.value) return;
    const sellingPlan = selectedOption.value;
    const cartonCount = Number.parseInt(selectedOption.dataset.cartonCount || '1', 10);
    const quantity = Number.isNaN(cartonCount) || cartonCount <= 0 ? 1 : cartonCount;

    const planDaysMap = this.#getNroPlanDaysMap(root);
    const mappedPlanDays = planDaysMap.get(sellingPlan);
    const cycleDays = selectedOption.dataset.planDays
      || (mappedPlanDays ? String(Math.max(1, Math.floor(mappedPlanDays))) : '');
    const usageValue = usageSelect instanceof HTMLSelectElement ? usageSelect.value || '' : '';

    /** @type {Record<string, string>} */
    const properties = {};
    if (usageValue) properties._nro_usage = usageValue;
    if (cycleDays) properties._nro_cycle_days = cycleDays;
    properties._nro_carton_count = String(quantity);

    applyButton.disabled = true;
    applyButton.classList.add('is-loading');
    applyButton.setAttribute('aria-busy', 'true');
    this.updateQuantity({
      line,
      quantity,
      sellingPlan,
      properties,
      action: 'change',
    });
  }

  #toggleNroPanel(panel, shouldOpen) {
    panel.style.transition = 'max-height var(--animation-speed) var(--animation-easing), opacity var(--animation-speed) var(--animation-easing)';
    panel.style.overflow = 'hidden';

    if (shouldOpen) {
      panel.classList.remove('hidden');
      panel.style.opacity = '0';
      panel.style.maxHeight = '0px';
      void panel.offsetHeight;
      panel.style.opacity = '1';
      panel.style.maxHeight = `${panel.scrollHeight}px`;
      const onOpenEnd = () => {
        panel.style.maxHeight = 'none';
        panel.removeEventListener('transitionend', onOpenEnd);
      };
      panel.addEventListener('transitionend', onOpenEnd);
      return;
    }

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    panel.style.opacity = '1';
    void panel.offsetHeight;
    panel.style.maxHeight = '0px';
    panel.style.opacity = '0';
    const onCloseEnd = () => {
      panel.classList.add('hidden');
      panel.removeEventListener('transitionend', onCloseEnd);
    };
    panel.addEventListener('transitionend', onCloseEnd);
  }

  #updateNroDiscountLabel(root) {
    const discountLabel = root.querySelector('[data-cart-nro-discount-percent]');
    const planSelect = root.querySelector('[data-cart-nro-plan]');
    if (!(discountLabel instanceof HTMLElement) || !(planSelect instanceof HTMLSelectElement)) return;

    const selectedOption = planSelect.options[planSelect.selectedIndex];
    const rawPercent = Number.parseFloat(selectedOption?.dataset.discountPercent || '0');
    if (!Number.isFinite(rawPercent) || rawPercent <= 0) return;
    discountLabel.textContent = String(Math.round(rawPercent));
  }

  #updateNroPlanLabels(root) {
    const usageSelect = root.querySelector('[data-cart-nro-usage]');
    const planSelect = root.querySelector('[data-cart-nro-plan]');
    if (!(usageSelect instanceof HTMLSelectElement) || !(planSelect instanceof HTMLSelectElement)) return;

    restoreStashedSelectOptions(planSelect);

    const piecesPerVariant = Number.parseInt(root.dataset.piecesPerVariant || '0', 10);

    // NRO min/max constraints (rendered server-side from variant metafields,
    // matching the PDP logic in snippets/variant-main-picker.liquid).
    //   min: minimum NUMBER OF PIECES per cycle (NRO.minimum_order_amount).
    //   max: maximum NUMBER OF PACKS per cycle, derived from the matching
    //        carton's pieces_per_variant.
    //   maxCartonCount: global theme-setting cap on cartons per delivery
    //                   (settings.nro_max_cartons_per_delivery), only emitted
    //                   for Carton variants.
    // Each is 0 when its underlying source isn't set.
    const nroMinPieces = Number.parseInt(root.dataset.nroMinPieces || '0', 10);
    const nroMaxPacks = Number.parseInt(root.dataset.nroMaxPacks || '0', 10);
    const nroMaxCartonCount = Number.parseInt(root.dataset.nroMaxCartonCount || '0', 10);
    const nroMaxUsage = Number.parseInt(root.dataset.nroMaxUsage || `${USAGE_HARD_CEILING}`, 10);

    const planDaysMap = this.#getNroPlanDaysMap(root);
    const options = Array.from(planSelect.options);
    const planDaysValues = Array.from(planDaysMap.values()).filter((value) => Number.isFinite(value) && value > 0);
    const maxPlanDays = planDaysValues.length ? Math.max(...planDaysValues) : 0;
    const toleranceDays = 7;

    // Cap the "pieces per day" dropdown to the range of usage values that
    // produce at least one visible plan.
    syncCartNroUsageRange(usageSelect, {
      piecesPerVariant,
      planDaysValues,
      nroMinPieces,
      nroMaxPacks,
      nroMaxCartonCount,
      maxUsage: nroMaxUsage,
      toleranceDays,
    });

    const usageCount = Math.max(1, Number.parseInt(usageSelect.value || '1', 10));
    const bestOptionByCarton = new Map();

    for (const option of options) {
      const planId = (option.dataset.sellingPlanId || option.value || '').toString();
      const mappedPlanDays = planDaysMap.get(planId);
      if (!mappedPlanDays || Number.isNaN(mappedPlanDays) || piecesPerVariant <= 0) continue;

      const cartonFloat = (mappedPlanDays * usageCount) / piecesPerVariant;
      const cartonCount = Math.max(1, Math.round(cartonFloat));
      const expectedPlanDays = (cartonCount * piecesPerVariant) / usageCount;
      const dayDiff = Math.abs(mappedPlanDays - expectedPlanDays);

      if (dayDiff > toleranceDays) continue;

      const currentBest = bestOptionByCarton.get(cartonCount);
      if (!currentBest || dayDiff < currentBest.dayDiff) {
        bestOptionByCarton.set(cartonCount, { option, dayDiff });
      }
    }

    for (const option of options) {
      const planId = (option.dataset.sellingPlanId || option.value || '').toString();
      const planName = option.dataset.planName || option.textContent || '';
      const mappedPlanDays = planDaysMap.get(planId);

      if (planId && !planDaysMap.has(planId)) {
        option.hidden = true;
        continue;
      }

      if (!mappedPlanDays || Number.isNaN(mappedPlanDays)) {
        option.hidden = false;
        option.textContent = planName;
        continue;
      }

      if (maxPlanDays && mappedPlanDays > maxPlanDays) {
        option.hidden = true;
        continue;
      }

      if (piecesPerVariant > 0) {
        const cartonCount = Math.max(1, Math.round((mappedPlanDays * usageCount) / piecesPerVariant));
        const best = bestOptionByCarton.get(cartonCount);
        if (!best || best.option !== option) {
          option.hidden = true;
          continue;
        }
        // NRO minimum: cartonCount * piecesPerVariant must reach nroMinPieces.
        if (nroMinPieces > 0 && cartonCount * piecesPerVariant < nroMinPieces) {
          option.hidden = true;
          continue;
        }
        // NRO maximum: cartonCount must not exceed nroMaxPacks.
        if (nroMaxPacks > 0 && cartonCount > nroMaxPacks) {
          option.hidden = true;
          continue;
        }
        // Global theme-setting cap on cartons per delivery (Carton variants only).
        if (nroMaxCartonCount > 0 && cartonCount > nroMaxCartonCount) {
          option.hidden = true;
          continue;
        }
        option.dataset.cartonCount = cartonCount.toString();
        const displayDays = Math.max(1, Math.floor(mappedPlanDays));
        option.textContent = formatCartonPerDaysLabel(root, cartonCount, displayDays);
      } else {
        option.textContent = planName;
      }
      option.hidden = false;
    }

    const selectedOption = planSelect.options[planSelect.selectedIndex];
    if (selectedOption?.hidden) {
      const firstVisibleOption = Array.from(planSelect.options).find((option) => !option.hidden);
      if (firstVisibleOption) {
        planSelect.value = firstVisibleOption.value;
      }
    }

    this.#sortNroPlanOptions(planSelect);
    detachHiddenSelectOptions(planSelect);
  }

  #hydrateNroPlanDays(root) {
    const planSelect = root.querySelector('[data-cart-nro-plan]');
    if (!(planSelect instanceof HTMLSelectElement)) return;
    const planDaysMap = this.#getNroPlanDaysMap(root);
    if (!planDaysMap.size) return;

    for (const option of Array.from(planSelect.options)) {
      const planId = (option.dataset.sellingPlanId || option.value || '').toString();
      const mappedPlanDays = planDaysMap.get(planId);
      if (!mappedPlanDays) continue;
      option.dataset.planDays = String(mappedPlanDays);
    }
  }

  #getNroPlanDaysMap(root) {
    const matrixScript = root.querySelector('[data-cart-nro-matrix]');
    if (!(matrixScript instanceof HTMLScriptElement) || !matrixScript.textContent) return new Map();

    try {
      const parsed = JSON.parse(matrixScript.textContent);
      const groups = Array.isArray(parsed) ? parsed : parsed?.selling_plan_groups || [];
      const planDaysMap = new Map();

      for (const group of groups) {
        const sellingPlans = group?.selling_plans || [];
        for (const sellingPlan of sellingPlans) {
          const planId = sellingPlan?.id?.toString();
          if (!planId) continue;
          const planDays = this.#getDaysFromSellingPlan(sellingPlan);
          if (planDays > 0) {
            planDaysMap.set(planId, planDays);
          }
        }
      }

      return planDaysMap;
    } catch (error) {
      console.warn('Invalid cart NRO matrix', error);
      return new Map();
    }
  }

  #intervalToDays(interval, count) {
    const intervalCount = Number(count) || 0;
    if (intervalCount <= 0) return 0;

    switch (interval) {
      case 'week':
        return intervalCount * 7;
      case 'month':
        return intervalCount * 30;
      case 'year':
        return intervalCount * 365;
      default:
        return intervalCount;
    }
  }

  #getDaysFromSellingPlan(sellingPlan) {
    const deliveryPolicy = sellingPlan?.delivery_policy;
    if (deliveryPolicy?.interval_count > 0) {
      const days = this.#intervalToDays(deliveryPolicy.interval, deliveryPolicy.interval_count);
      if (days > 0) return days;
    }

    const billingPolicy = sellingPlan?.billing_policy;
    if (billingPolicy?.interval_count > 0) {
      const days = this.#intervalToDays(billingPolicy.interval, billingPolicy.interval_count);
      if (days > 0) return days;
    }

    const rawValue = sellingPlan?.options?.[0]?.values?.[0] || sellingPlan?.name || '';
    return this.#extractDays(rawValue);
  }

  #extractDays(rawValue) {
    const raw = rawValue.toString();
    const dayMatch = raw.match(/(\d+)\s*day/i);
    if (dayMatch) return Number.parseInt(dayMatch[1], 10);
    const match = raw.match(/(\d+)/);
    if (!match) return 0;
    return Number.parseInt(match[1], 10);
  }

  #sortNroPlanOptions(planSelect) {
    const options = Array.from(planSelect.options);
    options.sort((a, b) => {
      const aCartons = Number.parseInt(a.dataset.cartonCount || '', 10);
      const bCartons = Number.parseInt(b.dataset.cartonCount || '', 10);
      const aValue = Number.isNaN(aCartons) ? Number.MAX_SAFE_INTEGER : aCartons;
      const bValue = Number.isNaN(bCartons) ? Number.MAX_SAFE_INTEGER : bCartons;
      return aValue - bValue;
    });

    const fragment = document.createDocumentFragment();
    options.forEach((option) => fragment.appendChild(option));
    planSelect.appendChild(fragment);
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
